import { EmptyState } from "@/components/molecules/empty-state";
import type { SessionListItem } from "@/features/sessions";
import { ClientScopeProvider, useClientId } from "@/features/utils/api-scope";
import { useTranslation } from "@/features/utils/i18n";
import { ConversationList } from "@/views/backoffice/calls/components/conversation-list";
import { ConversationView } from "@/views/backoffice/calls/components/conversation-view";
import { ChatsCircleIcon } from "@phosphor-icons/react";
import { useNavigate, useParams } from "react-router-dom";

/**
 * The client's calls, as the client reads them (`views/backoffice/calls`): the
 * same list, the same conversation, through the staff mount and read only — no
 * "call" button, no composer.
 *
 * The selection is the URL here too (`calls/session/:sessionId`), so a
 * conversation can be linked to from anywhere in the back-office.
 */
export function ClientCallsTab() {
  const { t } = useTranslation("calls");
  const navigate = useNavigate();
  const clientId = useClientId();
  const { sessionId } = useParams<{ sessionId: string }>();

  const base = `/admin/clients/${clientId}/calls`;
  const openSession = (session: SessionListItem) =>
    navigate(`${base}/session/${session.id}`);

  return (
    <ClientScopeProvider clientId={clientId} scope="staff" readOnly>
      {/* The tenant page fills the whole window; here it sits under the file's
          header and tabs, so it gets a frame and a height of its own. */}
      <div className="flex h-[calc(100dvh-16rem)] min-h-[32rem] overflow-hidden rounded-lg border bg-white dark:bg-slate-950">
        <div
          className={`min-h-0 w-full flex-col border-r md:flex md:w-[22rem] md:shrink-0 lg:w-[26rem] ${sessionId ? "hidden" : "flex"}`}
        >
          <ConversationList selectedId={sessionId} onSelect={openSession} />
        </div>

        <div
          className={`min-h-0 min-w-0 flex-1 flex-col ${sessionId ? "flex" : "hidden md:flex"}`}
        >
          {sessionId ? (
            <ConversationView
              key={sessionId}
              sessionId={sessionId}
              onBack={() => navigate(base)}
            />
          ) : (
            <EmptyState
              compact
              icon={ChatsCircleIcon}
              title={t("list.selectPrompt")}
              className="h-full"
            />
          )}
        </div>
      </div>
    </ClientScopeProvider>
  );
}
